import type {
  PartnerRequirement,
  PartnerSeason,
  RequirementCompletion,
} from '../types';

function cleanUrl(value?: string | null): string | null {
  return String(value || '').trim() || null;
}

export function effectiveRegistrationLink(
  locationId: string,
  locationDefault: string | null | undefined,
  season: PartnerSeason | null | undefined,
  partnerDefault?: string | null
): string | null {
  const override = cleanUrl(season?.registrationLinkOverridesByLocationId?.[locationId]);
  return override || cleanUrl(locationDefault) || cleanUrl(partnerDefault);
}

function dateMillis(value: unknown): number | null {
  if (!value) return null;
  if (value instanceof Date) return value.getTime();
  if (typeof (value as any)?.toMillis === 'function') return Number((value as any).toMillis());
  if (typeof (value as any)?.toDate === 'function') return (value as any).toDate().getTime();
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : null;
}

export function summarizeTeacherClearance(params: {
  requirements: PartnerRequirement[];
  completions: Record<string, RequirementCompletion>;
  locationId?: string | null;
  now?: Date;
  expiringWithinDays?: number;
}): {
  cleared: boolean;
  missingRequirementIds: string[];
  expiringRequirementIds: string[];
} {
  const {
    requirements,
    completions,
    locationId,
    now = new Date(),
    expiringWithinDays = 30,
  } = params;
  const nowMs = now.getTime();
  const expiringThreshold = nowMs + Math.max(0, expiringWithinDays) * 24 * 60 * 60 * 1000;
  const missingRequirementIds: string[] = [];
  const expiringRequirementIds: string[] = [];

  for (const requirement of requirements) {
    if (!requirement.active) continue;
    const override = locationId ? requirement.locationOverrides?.[locationId] : undefined;
    const required = override?.required ?? requirement.required;
    if (!required) continue;

    const completion = completions[requirement.id];
    const status = completion?.status;
    const expiresAt = dateMillis(completion?.expiresAt);
    const expired = expiresAt != null && expiresAt <= nowMs;
    const complete = (status === 'verified' || status === 'not_required') && !expired;

    if (!complete) {
      missingRequirementIds.push(requirement.id);
      continue;
    }
    if (expiresAt != null && expiresAt <= expiringThreshold) {
      expiringRequirementIds.push(requirement.id);
    }
  }

  return {
    cleared: missingRequirementIds.length === 0,
    missingRequirementIds,
    expiringRequirementIds,
  };
}
